import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { DataTable, ExportButton, Breadcrumbs, BreadcrumbItem } from '../components/ui';
import type { Column } from '../components/ui/DataTable';
import { toast, alert as sweetAlert } from '../utils/toast';
import type { Client } from '../types';
import { Users, Search, X, Eye, Ban, CheckCircle } from 'lucide-react';

export const ClientManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setOffset(0); // Reset pagination on search
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setOffset(0);
  }, [statusFilter, limit]);

  const { data, isLoading } = useQuery({
    queryKey: ['clients', statusFilter, debouncedSearch, offset, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await api.get<{ data: Client[]; meta: { total: number } }>(
        `/admin/clients?${params.toString()}`
      );
      return response.data;
    },
  });

  const clients = data?.data || [];
  const total = data?.meta?.total || 0;

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setOffset(0);
  };

  const suspendMutation = useMutation({
    mutationFn: async (clientId: string) => {
      await api.put(`/admin/clients/${clientId}/status`, { status: 'suspended' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Cliente suspenso com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao suspender o cliente.');
    },
  });

  const unsuspendMutation = useMutation({
    mutationFn: async (clientId: string) => {
      await api.put(`/admin/clients/${clientId}/status`, { status: 'approved' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Suspensão do cliente revogada com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao revogar suspensão.');
    },
  });

  const handleSuspend = async (client: Client) => {
    const result = await sweetAlert.confirm(
      'Suspender Cliente?',
      `Tem a certeza de que deseja suspender "${client.company_name}"? Isto irá bloquear todo o acesso à API.`
    );
    if (result.isConfirmed) {
      suspendMutation.mutate(client.id);
    }
  };

  const handleUnsuspend = async (client: Client) => {
    const result = await sweetAlert.confirm(
      'Reativar Cliente?',
      `Tem a certeza de que deseja reativar "${client.company_name}"? Isto irá restaurar o acesso à API.`
    );
    if (result.isConfirmed) {
      unsuspendMutation.mutate(client.id);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return 'badge-modern badge-success';
      case 'pending':
        return 'badge-modern badge-warning';
      case 'rejected':
        return 'badge-modern badge-danger';
      case 'suspended':
        return 'badge-modern badge-secondary';
      default:
        return 'badge-modern badge-secondary';
    }
  };

  const getStatusLabelPT = (status: string) => {
    const labels: Record<string, string> = {
      approved: 'Aprovado',
      pending: 'Pendente',
      rejected: 'Rejeitado',
      suspended: 'Suspenso',
    };
    return labels[status] || status;
  };

  const columns: Column<Client>[] = [
    {
      key: 'company_name',
      header: 'Empresa',
      render: (client) => (
        <Link
          to={`/clients/${client.id}`}
          className="text-primary-600 dark:text-primary-400 hover:underline font-bold"
        >
          {client.company_name}
        </Link>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (client) => (
        <span className="text-slate-600 dark:text-slate-300 font-medium">{client.email}</span>
      ),
    },
    {
      key: 'tax_id',
      header: 'NIF',
      render: (client) => (
        <span className="font-mono text-xs text-slate-700 dark:text-slate-300">
          {client.tax_id || '-'}
        </span>
      ),
    },
    {
      key: 'country',
      header: 'País',
      render: (client) => (
        <span className="text-slate-700 dark:text-slate-300">{client.country}</span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (client) => (
        <span className={getStatusBadge(client.status)}>{getStatusLabelPT(client.status)}</span>
      ),
    },
    {
      key: 'created_at',
      header: 'Registado em',
      render: (client) => (
        <span className="text-slate-500 dark:text-slate-400 text-xs">
          {new Date(client.created_at).toLocaleDateString('pt-PT')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (client) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/clients/${client.id}`}
            className="p-1.5 rounded-lg text-slate-500 hover:text-primary-600 dark:text-slate-400 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Ver Detalhes do Cliente"
          >
            <Eye className="w-4 h-4" />
          </Link>
          {client.status === 'approved' && (
            <button
              type="button"
              onClick={() => handleSuspend(client)}
              disabled={suspendMutation.isPending}
              className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 dark:text-slate-400 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
              title="Suspender Cliente"
            >
              <Ban className="w-4 h-4" />
            </button>
          )}
          {client.status === 'suspended' && (
            <button
              type="button"
              onClick={() => handleUnsuspend(client)}
              disabled={unsuspendMutation.isPending}
              className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 dark:text-slate-400 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
              title="Reativar Cliente"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const exportHeaders = [
    { key: 'company_name', label: 'Empresa' },
    { key: 'tax_id', label: 'NIF' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Telefone' },
    { key: 'country', label: 'País' },
    { key: 'status', label: 'Estado' },
    { key: 'created_at', label: 'Registado em' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Clientes</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Users className="w-8 h-8 text-primary-500" />
            <span>Gestão de Clientes</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Visualize, filtre e gira permissões de todos os clientes corporativos registados
          </p>
        </div>
        <ExportButton
          data={clients}
          filename={`clients_${Date.now()}.csv`}
          headers={exportHeaders}
        />
      </div>

      {/* Modern Search and Filters Bar */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-800 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Segmented Status Filters */}
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold">
            {[
              { id: 'all', label: 'Todos' },
              { id: 'approved', label: 'Aprovados' },
              { id: 'suspended', label: 'Suspensos' },
              { id: 'rejected', label: 'Rejeitados' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleStatusFilter(tab.id)}
                className={`px-3.5 py-1.5 rounded-lg transition-all ${
                  statusFilter === tab.id
                    ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-2xs font-bold'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="flex-1 md:max-w-md relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Pesquisar por nome, email ou NIF..."
              className="w-full pl-10 pr-9 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                title="Limpar pesquisa"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={clients}
        isLoading={isLoading}
        total={total}
        limit={limit}
        offset={offset}
        onPageChange={setOffset}
        onLimitChange={setLimit}
        emptyTitle="Nenhum cliente encontrado"
        emptyDescription="Tente ajustar os seus filtros de pesquisa ou utilize outros termos."
      />
    </div>
  );
};
